import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/admin/ImageUpload";

/**
 * Reusable per-item / per-page SEO editor block.
 * Renders 4 fields (title, description, keywords, og_image) + optional noIndex switch.
 * Values are read/written on the parent form via getters/setters.
 */
export default function SEOFields({
  values = {},
  onChange,
  showNoIndex = false,
  titlePlaceholder = "Optional — falls back to the item title",
  descriptionPlaceholder = "Optional — 150–160 characters recommended",
  testIdPrefix = "seo",
}) {
  const set = (k) => (e) => onChange({ ...values, [k]: e?.target ? e.target.value : e });
  return (
    <div className="space-y-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-4">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Search / Social Meta (optional)</div>
      <div>
        <Label htmlFor={`${testIdPrefix}-title`}>Meta title</Label>
        <Input
          id={`${testIdPrefix}-title`}
          value={values.meta_title || values.title || ""}
          onChange={set(values.meta_title !== undefined ? "meta_title" : "title")}
          placeholder={titlePlaceholder}
          data-testid={`${testIdPrefix}-title`}
        />
      </div>
      <div>
        <Label htmlFor={`${testIdPrefix}-desc`}>Meta description</Label>
        <Textarea
          id={`${testIdPrefix}-desc`}
          rows={3}
          value={values.meta_description || values.description || ""}
          onChange={set(values.meta_description !== undefined ? "meta_description" : "description")}
          placeholder={descriptionPlaceholder}
          data-testid={`${testIdPrefix}-desc`}
        />
      </div>
      <div>
        <Label htmlFor={`${testIdPrefix}-keywords`}>Keywords (comma separated)</Label>
        <Input
          id={`${testIdPrefix}-keywords`}
          value={values.meta_keywords || values.keywords || ""}
          onChange={set(values.meta_keywords !== undefined ? "meta_keywords" : "keywords")}
          placeholder="pharma, formulation, ..."
          data-testid={`${testIdPrefix}-keywords`}
        />
      </div>
      <ImageUpload
        label="Open Graph / social share image"
        value={values.og_image}
        onChange={(v) => onChange({ ...values, og_image: v })}
        folder="og"
        testId={`${testIdPrefix}-ogimage`}
        helper="1200×630 recommended. Falls back to the page's cover image or the site logo."
      />
      {showNoIndex && (
        <div className="flex items-center gap-3">
          <Switch
            checked={!!values.no_index}
            onCheckedChange={(v) => onChange({ ...values, no_index: v })}
            data-testid={`${testIdPrefix}-noindex`}
          />
          <span className="text-sm text-slate-700">
            {values.no_index ? "Excluded from search engines" : "Indexed by search engines"}
          </span>
        </div>
      )}
    </div>
  );
}
